import { motion, AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import { hapticLight, hapticSuccess, playGemsSound } from '@/lib/native'

interface GemParticle {
  id: number
  startX: number
  startY: number
  angle: number
  distance: number
  delay: number
}

interface GemParticlesProps {
  active: boolean
  amount: number
  targetPosition: { x: number; y: number } | null
  silent?: boolean // If true, skip sound (for pomodoro where we have separate sounds)
}

export function GemParticles({ active, amount, targetPosition, silent = false }: GemParticlesProps) {
  // Ensure amount is a valid number (defensive against NaN)
  const safeAmount = typeof amount === 'number' && !isNaN(amount) && isFinite(amount) ? amount : 0
  
  // Trigger haptics and sound when animation starts and completes
  useEffect(() => {
    if (active && targetPosition) {
      // Light haptic when particles spawn
      hapticLight()
      // Play gems collection sound (unless silent mode)
      if (!silent) {
        playGemsSound()
      }
      
      // Success haptic when particles reach target (after animation duration)
      const successTimeout = setTimeout(() => {
        hapticSuccess()
      }, 1200) // Match the animation duration
      
      return () => clearTimeout(successTimeout)
    }
  }, [active, targetPosition, silent])
  
  if (!active || !targetPosition || safeAmount <= 0) return null
  
  // Generate 5-10 gem particles
  const particleCount = Math.min(Math.max(Math.floor(safeAmount / 2), 5), 10)
  
  // Get screen center as starting point
  const startX = typeof window !== 'undefined' ? window.innerWidth / 2 : 0
  const startY = typeof window !== 'undefined' ? window.innerHeight / 2 : 0
  
  // Generate particles with random explosion angles
  const particles: GemParticle[] = Array.from({ length: particleCount }, (_, i) => {
    const angle = (i / particleCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
    const distance = 80 + Math.random() * 60
    const delay = Math.random() * 0.15
    
    return {
      id: i,
      startX,
      startY,
      angle,
      distance,
      delay,
    }
  })
  
  return (
    <div className="fixed inset-0 pointer-events-none z-[200]">
      <AnimatePresence>
        {particles.map((particle) => {
          // Calculate explosion point
          const explosionX = particle.startX + Math.cos(particle.angle) * particle.distance
          const explosionY = particle.startY + Math.sin(particle.angle) * particle.distance
          
          // Calculate path to target (curved bezier)
          const controlX = (explosionX + targetPosition.x) / 2 + (Math.random() - 0.5) * 100
          const controlY = (explosionY + targetPosition.y) / 2 - 80 // Arc upward
          
          return (
            <motion.div
              key={`gem-particle-${particle.id}`}
              initial={{
                x: particle.startX,
                y: particle.startY,
                scale: 0,
                opacity: 0,
              }}
              animate={{
                x: [
                  particle.startX,
                  explosionX,
                  controlX,
                  targetPosition.x,
                ],
                y: [
                  particle.startY,
                  explosionY,
                  controlY,
                  targetPosition.y,
                ],
                scale: [0, 1.2, 1, 0.5],
                opacity: [0, 1, 1, 0],
                rotate: [0, Math.random() * 360, Math.random() * 720],
              }}
              transition={{
                duration: 1.2,
                delay: particle.delay,
                times: [0, 0.2, 0.6, 1],
                ease: [0.34, 1.56, 0.64, 1],
              }}
              exit={{ opacity: 0, scale: 0 }}
              className="absolute -translate-x-1/2 -translate-y-1/2"
            >
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 0.3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <FluentEmoji emoji="💎" size={24} animated />
              </motion.div>
              
              {/* Sparkle trail */}
              <motion.div
                className="absolute inset-0 rounded-full bg-cyan-400/30 blur-lg"
                initial={{ scale: 1, opacity: 0.6 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ duration: 0.4, repeat: Infinity }}
              />
            </motion.div>
          )
        })}
        
        {/* Amount indicator at center - use wrapper for centering to avoid transform conflict */}
        <div
          key="gem-amount-wrapper"
          className="absolute"
          style={{
            left: startX,
            top: startY,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 0.5,
                repeat: 2,
              }}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 px-4 py-2 rounded-full shadow-2xl"
            >
              <span className="text-lg font-bold text-white">+{safeAmount}</span>
              <FluentEmoji emoji="💎" size={20} animated />
            </motion.div>
          </motion.div>
        </div>
      </AnimatePresence>
    </div>
  )
}
